import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  createContest,
  getActiveContests,
  getPosts,
  deletePost,
} from '../services/api';
import { Calendar, Trash2, Plus, RefreshCw } from 'lucide-react';

const AdminDashboardPage = () => {
  const { getToken } = useAuth();
  const [activeTab, setActiveTab] = useState('create'); // 'create', 'contests', 'posts'
  const [contests, setContests] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contestTitle, setContestTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [message, setMessage] = useState('');

  const withToken = useCallback(async (apiCall, ...args) => {
    const token = await getToken();
    return apiCall(...args, token);
  }, [getToken]);

  const fetchContests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await withToken(getActiveContests);
      setContests(data);
    } catch (err) {
      console.error('Failed to fetch contests:', err);
    } finally {
      setLoading(false);
    }
  }, [withToken]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await withToken(getPosts, 1, 50); // fetch recent 50 posts
      setPosts(data.posts || []);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  }, [withToken]);

  useEffect(() => {
    if (activeTab === 'contests') fetchContests();
    if (activeTab === 'posts') fetchPosts();
  }, [activeTab, fetchContests, fetchPosts]);

  const handleCreateContest = async (e) => {
    e.preventDefault();
    if (!contestTitle || !startDate || !endDate) {
      setMessage('All fields are required');
      return;
    }
    setSubmitting(true);
    setMessage('');
    try {
      await withToken(createContest, {
        title: contestTitle,
        start_date: Math.floor(new Date(startDate).getTime() / 1000),
        end_date: Math.floor(new Date(endDate).getTime() / 1000),
      });
      setMessage('Contest created successfully!');
      setContestTitle('');
      setStartDate('');
      setEndDate('');
      if (activeTab === 'contests') fetchContests();
    } catch (err) {
      setMessage(err.message || 'Failed to create contest');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await withToken(deletePost, postId);
      setPosts(posts.filter(p => p.id !== postId));
      setMessage('Post deleted successfully');
    } catch (err) {
      setMessage(err.message || 'Failed to delete post');
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      <h1 className="text-4xl font-bold mb-8 text-secondary">⚙️ Admin Dashboard</h1>

      {/* Tabs */}
      <div className="flex border-b-2 border-gray-200 mb-6 gap-2">
        <button
          onClick={() => setActiveTab('create')}
          className={`px-6 py-3 font-semibold transition ${activeTab === 'create' ? 'border-b-4 border-primary text-primary' : 'text-text-light hover:text-text'}`}
        >
          <Plus size={18} className="inline mr-2" /> Create Contest
        </button>
        <button
          onClick={() => setActiveTab('contests')}
          className={`px-6 py-3 font-semibold transition ${activeTab === 'contests' ? 'border-b-4 border-primary text-primary' : 'text-text-light hover:text-text'}`}
        >
          <Calendar size={18} className="inline mr-2" /> Active Contests
        </button>
        <button
          onClick={() => setActiveTab('posts')}
          className={`px-6 py-3 font-semibold transition ${activeTab === 'posts' ? 'border-b-4 border-primary text-primary' : 'text-text-light hover:text-text'}`}
        >
          🛡️ Moderate Posts
        </button>
      </div>

      {/* Create Contest Tab */}
      {activeTab === 'create' && (
        <div className="bg-surface rounded-lg shadow-md p-8 border-l-4 border-primary">
          <h2 className="text-2xl font-semibold mb-6 text-secondary">Create New Contest</h2>
          {message && <div className="mb-4 p-4 bg-primary/10 text-primary rounded-lg font-semibold">✨ {message}</div>}
          <form onSubmit={handleCreateContest}>
            <div className="mb-6">
              <label className="block text-text font-semibold mb-2">Contest Title</label>
              <input
                type="text"
                value={contestTitle}
                onChange={(e) => setContestTitle(e.target.value)}
                className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-primary focus:outline-none"
                required
              />
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-text font-semibold mb-2">Start Date & Time</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-primary focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-text font-semibold mb-2">End Date & Time</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-primary focus:outline-none"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary text-surface font-bold py-3 rounded-lg hover:bg-primary/90 transition disabled:bg-gray-400"
            >
              {submitting ? '⏳ Creating...' : '✨ Create Contest'}
            </button>
          </form>
        </div>
      )}

      {/* Active Contests Tab */}
      {activeTab === 'contests' && (
        <div className="bg-surface rounded-lg shadow-md p-8 border-l-4 border-secondary">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-secondary">Active Contests</h2>
            <button onClick={fetchContests} className="text-primary hover:text-primary/70 transition">
              <RefreshCw size={20} />
            </button>
          </div>
          {loading ? (
            <p className="text-text-light">⏳ Loading contests...</p>
          ) : contests.length === 0 ? (
            <p className="text-text-light text-center py-8">No active contests yet.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {contests.map(contest => (
                <li key={contest.id} className="py-4 hover:bg-background transition">
                  <p className="font-semibold text-text">{contest.title}</p>
                  <p className="text-sm text-text-light mt-1">
                    🕐 Ends: {formatDate(contest.end_date)} | Status: <span className="font-semibold text-accent">{contest.status}</span>
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Moderate Posts Tab */}
      {activeTab === 'posts' && (
        <div className="bg-surface rounded-lg shadow-md p-8 border-l-4 border-error">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-secondary">Recent Posts</h2>
            <button onClick={fetchPosts} className="text-error hover:text-error/70 transition">
              <RefreshCw size={20} />
            </button>
          </div>
          {loading ? (
            <p className="text-text-light">⏳ Loading posts...</p>
          ) : posts.length === 0 ? (
            <p className="text-text-light text-center py-8">No posts to moderate.</p>
          ) : (
            <ul className="divide-y divide-gray-200 space-y-4">
              {posts.map(post => (
                <li key={post.id} className="py-4 flex justify-between items-start hover:bg-background transition rounded-lg px-3">
                  <div className="flex-1">
                    <p className="text-sm text-text-light font-semibold">By {post.display_name}</p>
                    <p className="mt-2 text-text">{post.content?.substring(0, 150)}...</p>
                    <p className="text-xs text-text-light mt-2">📅 {formatDate(post.created_at)}</p>
                  </div>
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="ml-4 text-error hover:text-error/70 hover:bg-error/10 p-2 rounded transition"
                  >
                    <Trash2 size={20} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;